/**
 * Thin fetch wrapper. Attaches the session token from localStorage as
 * `Authorization: Bearer <token>` on every request.
 *
 * 401 responses bubble up as `ApiError(401, …)` — `AuthGuard` listens
 * for these to redirect the user to /login.
 */
import { getToken, clearToken } from '@/lib/authToken';

export const API_BASE =
  (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_ADMIN_API_URL) ||
  'http://localhost:8080/admin/v1';

export class ApiError extends Error {
  constructor(public status: number, message: string, public body?: unknown) {
    super(message);
    this.name = 'ApiError';
  }
}

let onUnauthorized: (() => void) | null = null;

export function setUnauthorizedHandler(fn: (() => void) | null) {
  onUnauthorized = fn;
}

export async function apiFetch<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const token = getToken();
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.headers ?? {}),
    },
  });
  const text = await res.text();
  const body = text ? safeJson(text) : null;
  if (!res.ok) {
    const detail = (body as any)?.detail ?? res.statusText;
    if (res.status === 401) {
      clearToken();
      onUnauthorized?.();
    }
    throw new ApiError(res.status, `${res.status} ${detail}`, body);
  }
  return body as T;
}

function safeJson(s: string): unknown {
  try {
    return JSON.parse(s);
  } catch {
    return s;
  }
}

export async function pingApi(): Promise<boolean> {
  try {
    await apiFetch('/health', { method: 'GET' });
    return true;
  } catch {
    return false;
  }
}
